import React, { useEffect, useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { Autocomplete } from "@react-google-maps/api";
import SearchSelect from "components/ui/SearchSelect";
import Button from "components/ui/Button";
import GoogleMaps from "components/GoogleMaps";
import { useUpdateContractorMutation } from "apis/contractor";

const SERVICE_OPTIONS = [
  { value: "photo", label: "Photo" },
  { value: "video", label: "Video" },
  { value: "drone", label: "Drone" },
  { value: "floorplan", label: "Floorplan" },
  { value: "floorplanRedraw", label: "Floorplan Redraw" },
  { value: "edit", label: "Edit" },
  { value: "print", label: "Print" },
  { value: "auction", label: "Auction" },
];

const CALENDAR_COLORS = [
  { value: "#D32F2F", label: "Red" },
  { value: "#C2185B", label: "Hot Pink" },
  { value: "#AD1457", label: "Cranberry" },
  { value: "#880E4F", label: "Magenta" },
  { value: "#6A1B9A", label: "Plum" },
  { value: "#4A148C", label: "Deep Violet" },
  { value: "#4527A0", label: "Purple" },
  { value: "#311B92", label: "Grape" },
  { value: "#1565C0", label: "Blue" },
  { value: "#0D47A1", label: "Royal Blue" },
  { value: "#1A237E", label: "Sapphire" },
  { value: "#0277BD", label: "Ocean Blue" },
  { value: "#00838F", label: "Teal" },
  { value: "#006064", label: "Dark Cyan" },
  { value: "#00695C", label: "Deep Teal" },
  { value: "#2E7D32", label: "Green" },
  { value: "#1B5E20", label: "Dark Green" },
  { value: "#33691E", label: "Olive" },
  { value: "#E65100", label: "Orange" },
  { value: "#BF360C", label: "Burnt Orange" },
  { value: "#8B4513", label: "Brown" },
  { value: "#37474F", label: "Slate" },
  { value: "#263238", label: "Charcoal" },
  { value: "#1E3A5F", label: "Navy" },
  { value: "#C62828", label: "Crimson" },
  { value: "#0288D1", label: "Sky Blue" },
  { value: "#0097A7", label: "Cyan" },
  { value: "#9E9D24", label: "Olive Green" },
  { value: "#FF8F00", label: "Amber" },
  { value: "#6D4C41", label: "Cocoa" },
  { value: "#455A64", label: "Blue Grey" },
];

const DAYS = [
  { key: "SUN", label: "Sun" },
  { key: "MON", label: "Mon" },
  { key: "TUE", label: "Tue" },
  { key: "WED", label: "Wed" },
  { key: "THU", label: "Thu" },
  { key: "FRI", label: "Fri" },
  { key: "SAT", label: "Sat" },
];

const DEFAULT_CENTER = { lat: -33.8688, lng: 151.2093 };

const SYDNEY_BOUNDS = {
  north: -33.4,
  south: -34.2,
  east: 151.5,
  west: 150.5,
};

const convertFrom24To12Hour = (time24h) => {
  if (!time24h) return "";
  const [hours, minutes] = time24h.split(":");
  const date = new Date(2024, 0, 1, hours, minutes);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const convertTo24Hour = (time12h) => {
  if (!time12h) return "";
  const [time, modifier] = time12h.split(" ");
  let [hours, minutes] = time.split(":");
  if (hours === "12") hours = "00";
  if (modifier === "PM" && hours !== "12") {
    hours = String(parseInt(hours, 10) + 12);
  }
  return `${hours.padStart(2, "0")}:${minutes}`;
};

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = new Date(2024, 0, 1, hour, minute);
      const label = time.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      options.push({ value: label, label });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

export default function ContractorEdit({
  user,
  selectedId,
  setIsEditOpen,
  companies = [],
}) {
  const companyOptions = companies.map((c) => ({
    value: c._id,
    label: c.name,
  }));

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: user?.name || "",
      companyId: user?.companyId?._id || user?.companyId || null,
      mobile: user?.mobile || "",
      services:
        user?.services?.map((service) => ({
          value: service,
          label:
            SERVICE_OPTIONS.find((o) => o.value === service)?.label || service,
        })) || [],
      calendarColor: user?.calendarColor || "",
    },
  });

  const { mutate: updateContractor, isPending } = useUpdateContractorMutation({
    onSuccess: () => setIsEditOpen(false),
  });

  // ─── Availability ───
  const [availability, setAvailability] = useState(() =>
    DAYS.reduce((acc, { key }) => {
      acc[key] = { available: false, startTime: "", endTime: "" };
      return acc;
    }, {}),
  );

  useEffect(() => {
    if (!user?.availability) return;
    const hydrated = Object.entries(user.availability).reduce(
      (acc, [day, data]) => {
        acc[day] = {
          available: !!data.available,
          startTime: convertFrom24To12Hour(data.startTime),
          endTime: convertFrom24To12Hour(data.endTime),
          duskEndTime: convertFrom24To12Hour(data.duskEndTime),
        };
        return acc;
      },
      {},
    );
    setAvailability((prev) => ({ ...prev, ...hydrated }));
  }, [user]);

  const updateAvailability = (day, field, value) => {
    setAvailability((prev) => {
      if (field === "available" && value === true) {
        return {
          ...prev,
          [day]: { available: true, startTime: "6:00 AM", endTime: "9:00 PM" },
        };
      }
      return { ...prev, [day]: { ...prev[day], [field]: value } };
    });
  };

  // ─── Priority area ───
  const [priorityArea, setPriorityArea] = useState(
    user?.priorityArea?.lat != null && user?.priorityArea?.lng != null
      ? { lat: user.priorityArea.lat, lng: user.priorityArea.lng }
      : DEFAULT_CENTER,
  );
  const [priorityRadius, setPriorityRadius] = useState(
    user?.priorityArea?.radius || 5000,
  );
  const [searchAutocomplete, setSearchAutocomplete] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [mapRef, setMapRef] = useState(null);

  const handleMapLoad = useCallback((map) => setMapRef(map), []);

  useEffect(() => {
    if (!mapRef) return;
    const listener = mapRef.addListener("click", (e) => {
      setPriorityArea({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });
    return () => listener.remove();
  }, [mapRef]);

  const selectedColor = watch("calendarColor");
  // Dusk shoots are part of the photo service
  const offersDusk = (watch("services") || []).some((s) => s.value === "photo");

  const onSubmit = (data) => {
    const payload = {
      id: selectedId,
      name: data.name,
      companyId: data.companyId || null,
      mobile: data.mobile,
      calendarColor: data.calendarColor,
      services: data.services.map((s) => s.value),
      priorityArea: { ...priorityArea, radius: priorityRadius },
      availability: Object.entries(availability).reduce((acc, [day, d]) => {
        acc[day] = {
          available: d.available,
          startTime: convertTo24Hour(d.startTime),
          endTime: convertTo24Hour(d.endTime),
        };
        // How late they finish a dusk shoot; left out when not set
        if (d.duskEndTime) acc[day].duskEndTime = convertTo24Hour(d.duskEndTime);
        return acc;
      }, {}),
    };
    updateContractor(payload);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* ─── Profile ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <h4 className="mb-3">Profile</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Name *</label>
            <input
              type="text"
              {...register("name", { required: "Name is required" })}
              placeholder="Full name"
              className={`form-input border ${
                errors.name ? "border-red-500" : "border-mediumgray"
              }`}
            />
            {errors.name && (
              <p className="form-error-message">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="form-label">Company</label>
            <Controller
              name="companyId"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  value={
                    companyOptions.find((o) => o.value === field.value) || null
                  }
                  onChange={(opt) => field.onChange(opt?.value || null)}
                  options={companyOptions}
                  placeholder="Select company..."
                  isClearable
                />
              )}
            />
          </div>

          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              value={user?.email || ""}
              readOnly
              disabled
              className="form-input border border-mediumgray bg-lightergray text-darkgray cursor-not-allowed"
            />
            <p className="text-xs text-darkgray mt-1">
              Email can't be changed — it's how they sign in.
            </p>
          </div>

          <div>
            <label className="form-label">Mobile *</label>
            <input
              type="tel"
              {...register("mobile", { required: "Mobile is required" })}
              placeholder="0400 000 000"
              className={`form-input border ${
                errors.mobile ? "border-red-500" : "border-mediumgray"
              }`}
            />
            {errors.mobile && (
              <p className="form-error-message">{errors.mobile.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* ─── Services ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <h4 className="mb-3">Services</h4>
        <Controller
          name="services"
          control={control}
          render={({ field }) => (
            <SearchSelect
              value={field.value}
              onChange={field.onChange}
              options={SERVICE_OPTIONS}
              multiple
              placeholder="Select services..."
            />
          )}
        />
        {errors.services && (
          <p className="form-error-message">{errors.services.message}</p>
        )}
      </div>

      {/* ─── Calendar Color ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <h4 className="mb-1">Calendar Color</h4>
        <p className="text-sm text-darkgray mb-3">
          Colour-codes this contractor's events on shared calendars.
        </p>
        <Controller
          name="calendarColor"
          control={control}
          render={({ field }) => (
            <div className="flex flex-wrap gap-3">
              {CALENDAR_COLORS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  onClick={() => field.onChange(value)}
                  className="w-8 h-8 rounded-full transition-all border border-mediumgray"
                  style={{
                    backgroundColor: value,
                    outline: field.value === value ? "2px solid #000" : "none",
                    outlineOffset: "2px",
                    transform:
                      field.value === value ? "scale(1.15)" : "scale(1)",
                  }}
                />
              ))}
            </div>
          )}
        />
        {selectedColor && (
          <p className="text-xs text-darkgray mt-2">
            Selected:{" "}
            {CALENDAR_COLORS.find((c) => c.value === selectedColor)?.label ||
              selectedColor}
          </p>
        )}
      </div>

      {/* ─── Priority Area ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <h4 className="mb-1">Priority Area</h4>
        <p className="text-sm text-darkgray mb-3">
          Search a Sydney suburb, click the map to reposition, or use the
          slider to resize coverage.
        </p>

        <Autocomplete
          onLoad={(autocomplete) => setSearchAutocomplete(autocomplete)}
          onPlaceChanged={() => {
            if (!searchAutocomplete) return;
            const place = searchAutocomplete.getPlace();
            if (place?.geometry?.location) {
              setPriorityArea({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              });
              setSearchValue(place.formatted_address || place.name || "");
            }
          }}
          options={{
            bounds: SYDNEY_BOUNDS,
            strictBounds: true,
            componentRestrictions: { country: "au" },
            types: ["(regions)"],
            fields: ["geometry", "formatted_address", "name"],
          }}
        >
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search suburb (e.g. Castle Hill)"
            className="form-input border border-mediumgray mb-3"
          />
        </Autocomplete>

        <GoogleMaps
          center={priorityArea}
          markers={[{ position: priorityArea, iconType: "black" }]}
          circles={[
            {
              center: priorityArea,
              radius: priorityRadius,
              fillColor: "#000000",
              fillOpacity: 0.15,
              strokeColor: "#000000",
              strokeWeight: 2,
            },
          ]}
          onMapLoad={handleMapLoad}
          options={{
            view: "roadmap",
            zoom: 11,
            mapStyle: "customGrey",
            disableDefaultUI: true,
            zoomControl: true,
            fullscreenControl: true,
          }}
        />

        <div className="mt-3 space-y-1">
          <div className="flex justify-between text-xs text-darkgray">
            <span>Radius</span>
            <span className="text-black font-medium">
              {priorityRadius >= 1000
                ? `${(priorityRadius / 1000).toFixed(1)} km`
                : `${priorityRadius} m`}
            </span>
          </div>
          <input
            type="range"
            min={500}
            max={50000}
            step={500}
            value={priorityRadius}
            onChange={(e) => setPriorityRadius(Number(e.target.value))}
            className="w-full accent-black"
          />
          <div className="flex justify-between text-xs text-darkgray">
            <span>500 m</span>
            <span>50 km</span>
          </div>
        </div>
      </div>

      {/* ─── Availability ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <h4 className="mb-1">Availability</h4>
        <p className="text-sm text-darkgray mb-3">
          Mark the days this contractor is available and set their hours.
        </p>

        <div className="space-y-2">
          {DAYS.map(({ key, label }) => {
            const day = availability[key] || {};
            return (
              <div
                key={key}
                className={`border border-mediumgray rounded-md p-3 ${
                  day.available ? "bg-lightergray" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer min-w-[80px]">
                    <input
                      type="checkbox"
                      checked={!!day.available}
                      onChange={() =>
                        updateAvailability(key, "available", !day.available)
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-black font-medium">{label}</span>
                  </label>

                  {day.available ? (
                    <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                      <div className="flex-1 min-w-[110px]">
                        <SearchSelect
                          value={
                            day.startTime
                              ? { value: day.startTime, label: day.startTime }
                              : null
                          }
                          onChange={(opt) =>
                            updateAvailability(key, "startTime", opt?.value)
                          }
                          options={TIME_OPTIONS}
                          placeholder="Start"
                        />
                      </div>
                      <span className="text-darkgray">–</span>
                      <div className="flex-1 min-w-[110px]">
                        <SearchSelect
                          value={
                            day.endTime
                              ? { value: day.endTime, label: day.endTime }
                              : null
                          }
                          onChange={(opt) =>
                            updateAvailability(key, "endTime", opt?.value)
                          }
                          options={TIME_OPTIONS}
                          placeholder="End"
                        />
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-darkgray">Unavailable</span>
                  )}
                </div>

                {day.available && offersDusk && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-darkgray">
                      Dusk shoots until
                    </span>
                    <div className="w-40">
                      <SearchSelect
                        value={
                          day.duskEndTime
                            ? { value: day.duskEndTime, label: day.duskEndTime }
                            : null
                        }
                        onChange={(opt) =>
                          updateAvailability(key, "duskEndTime", opt?.value)
                        }
                        options={TIME_OPTIONS}
                        placeholder="Not available"
                        isClearable
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Submit ─── */}
      <div className="flex justify-end">
        <Button type="submit" className="black-button" loading={isPending}>
          Save Profile
        </Button>
      </div>
    </form>
  );
}